import type {
  CurrentStatusState,
  DayBucket,
  DependabilityV2Tier,
  LastActiveBucket,
} from '@/services/workersV2API';
import type { GravyWorkersFilters } from './components/FilterRow';
import type { SortColumn, SortState } from './components/WorkersTable';
import {
  LAST_ACTIVE_OPTIONS,
  PROGRESS_OPTIONS,
  RADIUS,
  STATUS_OPTIONS,
  SUSPENSION_OPTIONS,
  SuspensionFilter,
  TAB_OPTIONS,
  TAX_TYPE_OPTIONS,
  TIER_OPTIONS,
} from './mappings';
import {
  CURRENT_STATUS_OPTIONS,
  DEFAULT_RANGE,
  RANGE_OPTIONS,
  RangeDays,
} from './onboardingMappings';

export type Tab = (typeof TAB_OPTIONS)[number]['value'];

// GRAVY-10975 WL-9: tab, range, search, filters, sort, page and the open
// worker live in the URL so a list can be shared and survives a reload.
// Unknown or malformed values fall back to the defaults instead of reaching
// the API.
export type GravyWorkersUrlState = {
  tab: Tab;
  range: RangeDays;
  search: string;
  filters: GravyWorkersFilters;
  sort: SortState;
  page: number;
  perPage: number;
  workerId: number | null;
};

export const DEFAULT_TAB: Tab = 'onboarding';
export const DEFAULT_SORT: SortState = { by: 'created_at', direction: 'desc' };
export const DEFAULT_PER_PAGE = 20;
// A page size from the URL must be one the paginator offers (its default
// itemOptions): any other shows "5" in its select (review D3-06), and one over
// graphy's 100-row cap left rows unreachable (D-11).
const PER_PAGE_OPTIONS = [5, 10, 15, 20];

const SORT_COLUMNS: SortColumn[] = [
  'id',
  'checkr_status',
  'i9_status',
  'average_rating',
  'tax_type',
  'shifts',
  'created_at',
  'onboarding_step',
  'stuck_for',
  'shifts_completed',
  'background_check_status',
  'i9_status_rank',
];

const pick = <T extends string>(
  raw: string | null,
  options: { value: T }[],
): T | undefined => options.find((o) => o.value === raw)?.value;

const positiveInt = (raw: string | null): number | null => {
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 ? n : null;
};

export const parseUrlState = (
  params: URLSearchParams,
): GravyWorkersUrlState => {
  const tab = pick(params.get('tab'), TAB_OPTIONS) ?? DEFAULT_TAB;
  const rangeRaw = Number(params.get('range'));
  const range = (RANGE_OPTIONS as readonly number[]).includes(rangeRaw)
    ? (rangeRaw as RangeDays)
    : DEFAULT_RANGE;

  const filters: GravyWorkersFilters = { radius: RADIUS.default };
  const tax = pick(params.get('tax_type'), TAX_TYPE_OPTIONS);
  if (tax) filters.tax_type = tax;
  const status = pick<CurrentStatusState>(
    params.get('status'),
    CURRENT_STATUS_OPTIONS,
  );
  if (status) filters.current_status = status;
  const progress = pick<DayBucket>(params.get('progress'), PROGRESS_OPTIONS);
  if (progress) filters.progress = progress;
  const lastActive = pick<LastActiveBucket>(
    params.get('last_active'),
    LAST_ACTIVE_OPTIONS,
  );
  if (lastActive) filters.last_active = lastActive;
  const active = pick(params.get('active'), STATUS_OPTIONS);
  if (active) filters.active = active;
  const suspension = pick<SuspensionFilter>(
    params.get('suspension'),
    SUSPENSION_OPTIONS,
  );
  if (suspension) filters.accountability_status = suspension;
  const tier = pick<DependabilityV2Tier>(params.get('tier'), TIER_OPTIONS);
  if (tier) filters.dependability_v2_tier = tier;
  const skills = (params.get('skills') ?? '')
    .split(',')
    .map((s) => positiveInt(s))
    .filter((n): n is number => n !== null);
  if (skills.length) filters.skill_ids = skills;
  const zip = (params.get('zip') ?? '').replace(/\D/g, '').slice(0, 5);
  if (zip) filters.zip = zip;
  const radius = positiveInt(params.get('radius'));
  if (radius && radius >= RADIUS.min && radius <= RADIUS.max)
    filters.radius = radius;

  const sortBy = SORT_COLUMNS.find((c) => c === params.get('sort'));
  const direction = params.get('dir') === 'asc' ? 'asc' : 'desc';

  return {
    tab,
    range,
    search: params.get('q') ?? '',
    filters,
    sort: sortBy ? { by: sortBy, direction } : DEFAULT_SORT,
    page: positiveInt(params.get('page')) ?? 1,
    perPage:
      PER_PAGE_OPTIONS.find((n) => n === Number(params.get('per_page'))) ??
      DEFAULT_PER_PAGE,
    workerId: positiveInt(params.get('worker')),
  };
};

// Only non-default values are written, so a fresh page has a clean URL.
export const serializeUrlState = (
  state: GravyWorkersUrlState,
): URLSearchParams => {
  const p = new URLSearchParams();
  const { filters } = state;
  if (state.tab !== DEFAULT_TAB) p.set('tab', state.tab);
  if (state.range !== DEFAULT_RANGE) p.set('range', String(state.range));
  if (state.search) p.set('q', state.search);
  if (filters.current_status) p.set('status', filters.current_status);
  if (filters.progress) p.set('progress', filters.progress);
  if (filters.tax_type) p.set('tax_type', filters.tax_type);
  if (filters.last_active) p.set('last_active', filters.last_active);
  if (filters.active) p.set('active', filters.active);
  if (filters.accountability_status)
    p.set('suspension', filters.accountability_status);
  if (filters.dependability_v2_tier)
    p.set('tier', filters.dependability_v2_tier);
  if (filters.skill_ids?.length) p.set('skills', filters.skill_ids.join(','));
  if (filters.zip) p.set('zip', filters.zip);
  if (filters.radius !== RADIUS.default)
    p.set('radius', String(filters.radius));
  if (
    state.sort.by !== DEFAULT_SORT.by ||
    state.sort.direction !== DEFAULT_SORT.direction
  ) {
    p.set('sort', state.sort.by);
    p.set('dir', state.sort.direction);
  }
  if (state.page > 1) p.set('page', String(state.page));
  if (state.perPage !== DEFAULT_PER_PAGE)
    p.set('per_page', String(state.perPage));
  if (state.workerId) p.set('worker', String(state.workerId));
  return p;
};
